/**
 * canoneMensile — quanto paga al mese un'azienda, voce per voce, IVA esclusa (02/11/2026).
 *
 * Nata dal primo addebito di Fortezza (01-02/11/2026): la console mostrava «MRR stima 824 €», cioè solo i posti, mentre
 * Stripe aveva fatturato 992 € perché nell'abbonamento ci sono anche il centralino AI (99 €) e l'agente WhatsApp (69 €).
 * Simone: «sarebbe bello che ci fosse il dettaglio del costo completo, dunque anche dei servizi aggiuntivi».
 *
 * Il canone ha le stesse tre famiglie di righe che il pagamento mette nell'abbonamento Stripe
 * (`functions/src/callable/billing/createCheckoutSession.ts`):
 *  1. i POSTI, piano per piano (`seat.quota_by_tier`, scritto dal webhook dagli item veri di Stripe; senza posti misti,
 *     `seat.quota` × il piano dell'azienda), al prezzo di `tier_definitions`;
 *  2. le APP A PERSONA comprate (`seat.app_quota_by_id`, scritto dal webhook), al prezzo di `app_addons`; un'app che la
 *     piattaforma regala a tutta l'azienda (`modules.override`) non ha posti e non costa;
 *  3. gli STRUMENTI A CANONE accesi (`strumentiAccesi`), alle condizioni che QUESTA azienda paga
 *     (`strumentoPerLAzienda`: prezzo di prima, su misura o listino); un regalo in corso vale zero. Regalo = un regalo
 *     con scadenza in `billing.regali` (`regaloDallAzienda`) oppure, come in `consumoDellAzienda`, uno strumento acceso
 *     dalla piattaforma (`modules.override`) e non comprato (`modules.addon`).
 * I consumi oltre la quota non sono canone: cambiano ogni mese e li dice la linguetta «Strumenti».
 *
 * Cosa NON fa, e lo dichiara invece di indovinare: lo sconto dell'abbonamento (`billing.discount_percent`) si riporta
 * ma non si applica, perché il webhook ne copia solo la percentuale e non a quali righe vale; l'esenzione la decide chi
 * chiama (come prima). I prezzi sono quelli mensili del listino.
 *
 * Puro: nessuna lettura, nessun prezzo nel codice. Lo usano l'elenco delle aziende e la scheda «Billing» della console.
 */
import { aziendaPerGliStrumenti, viaDiAttivazione } from '../strumenti/attivazione';
import { strumentiAccesi } from '../strumenti/consumoDellAzienda';
import { strumentoPerLAzienda, type StrumentoACanone } from '../strumenti/listino';
import { regaloDallAzienda } from '../strumenti/regalo';
import { POSTI, ePostoDellaScala } from './funzioniDelPosto';
import { naturaDellAzienda } from './naturaAzienda';

/** Il prezzo a posto al mese di un piano o di un'app, IVA esclusa, con la sua etichetta. */
export interface PrezzoAPosto {
  readonly etichetta: string;
  /** null = prezzo non ancora messo a listino. */
  readonly eur_mese: number | null;
}

/** I tre listini che compongono il canone, letti da chi chiama. */
export interface ListiniDelCanone {
  /** id del piano (`base`, `pro`, `business`, `free`) → prezzo (`tier_definitions`). */
  readonly piani: Readonly<Record<string, PrezzoAPosto>>;
  /** capability dell'app (`yumacall`) → prezzo (`app_addons`). */
  readonly app: Readonly<Record<string, PrezzoAPosto>>;
  /** capability dello strumento → voce del listino (`strumenti_a_canone`). */
  readonly strumenti: ReadonlyMap<string, StrumentoACanone>;
}

/** Come l'azienda paga uno strumento acceso. */
export type CondizioniDelloStrumento = 'listino' | 'prezzo_proprio' | 'regalo' | 'solo_consumo';

export type RigaDelCanone =
  | {
      readonly famiglia: 'posti' | 'app';
      readonly id: string;
      readonly etichetta: string;
      readonly quantita: number;
      readonly prezzo_centesimi: number;
      readonly importo_centesimi: number;
    }
  | {
      readonly famiglia: 'strumento';
      readonly id: string;
      readonly etichetta: string;
      readonly condizioni: CondizioniDelloStrumento;
      readonly importo_centesimi: number;
    };

export interface CanoneMensile {
  readonly righe: readonly RigaDelCanone[];
  /** Somma dei posti. */
  readonly posti_centesimi: number;
  /** Somma di app a persona e strumenti a canone. */
  readonly aggiunte_centesimi: number;
  /** Tutto, IVA esclusa, prima dell'eventuale sconto. */
  readonly totale_centesimi: number;
  /** Lo sconto dell'abbonamento copiato da Stripe (`billing.discount_percent`), NON applicato al totale. */
  readonly sconto_percentuale: number | null;
  /** Piani o app con posti comprati ma senza prezzo a listino: il totale li conta zero, e va detto. */
  readonly senza_prezzo: readonly string[];
}

type Mappa = Record<string, unknown>;
const mappa = (v: unknown): Mappa => (typeof v === 'object' && v !== null ? (v as Mappa) : {});
const intero = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
const centesimi = (eur: number): number => Math.round(eur * 100);

/**
 * PURA. I posti COMPRATI dall'azienda, piano per piano, dal più alto: posti misti (`seat.quota_by_tier`) se ci sono,
 * altrimenti tutti i posti (`seat.quota`) al piano dell'azienda. I posti fuori dalla scala (02/11/2026, Presenze) vengono
 * dopo quelli della scala, e si pagano come gli altri al prezzo del loro documento (`listini.piani.presenze`). La usano
 * il canone e il riepilogo dei posti del promemoria di fine prova (`riepilogoDeiPosti.ts`).
 */
export function postiCompratiPerPiano(datiAzienda: unknown): Array<[string, number]> {
  return postiPerPiano(mappa(datiAzienda));
}

function postiPerPiano(d: Mappa): Array<[string, number]> {
  const seat = mappa(d.seat);
  const perPiano = mappa(seat.quota_by_tier);
  const misti = POSTI.map((p): [string, number] => [p, intero(perPiano[p])]).filter(([, n]) => n > 0);
  // Dal piano più alto, come la console mostra i posti; i posti fuori dalla scala in fondo.
  if (misti.length > 0) return [...misti.filter(([p]) => ePostoDellaScala(p)).reverse(), ...misti.filter(([p]) => !ePostoDellaScala(p))];
  const quanti = intero(seat.quota);
  const piano = typeof d.tier === 'string' && d.tier !== '' ? d.tier : 'base';
  return quanti > 0 ? [[piano, quanti]] : [];
}

/** PURA. Il canone mensile dell'azienda (documento `tenants/{id}`), voce per voce, IVA esclusa. */
export function canoneMensileDellAzienda(datiAzienda: unknown, listini: ListiniDelCanone, oraMs: number): CanoneMensile {
  const d = mappa(datiAzienda);
  const righe: RigaDelCanone[] = [];
  const senzaPrezzo: string[] = [];

  for (const [piano, quantita] of postiPerPiano(d)) {
    const prezzo = listini.piani[piano];
    if (!prezzo || prezzo.eur_mese === null) senzaPrezzo.push(prezzo?.etichetta ?? piano);
    const unitario = centesimi(prezzo?.eur_mese ?? 0);
    righe.push({ famiglia: 'posti', id: piano, etichetta: prezzo?.etichetta ?? piano, quantita, prezzo_centesimi: unitario, importo_centesimi: unitario * quantita });
  }

  const moduli = mappa(d.modules);
  const regalate = new Set(Array.isArray(moduli.override) ? (moduli.override as unknown[]) : []);
  const comprate = new Set(Array.isArray(moduli.addon) ? (moduli.addon as unknown[]) : []);
  for (const [app, n] of Object.entries(mappa(mappa(d.seat).app_quota_by_id))) {
    const quantita = intero(n);
    if (quantita === 0 || regalate.has(app)) continue;
    const prezzo = listini.app[app];
    if (!prezzo || prezzo.eur_mese === null) senzaPrezzo.push(prezzo?.etichetta ?? app);
    const unitario = centesimi(prezzo?.eur_mese ?? 0);
    righe.push({ famiglia: 'app', id: app, etichetta: prezzo?.etichetta ?? app, quantita, prezzo_centesimi: unitario, importo_centesimi: unitario * quantita });
  }

  for (const voce of strumentiAccesi(datiAzienda, listini.strumenti)) {
    const suo = strumentoPerLAzienda(datiAzienda, voce);
    const regalo = regaloDallAzienda(datiAzienda, voce.capability, oraMs) !== null
      || (regalate.has(voce.capability) && !comprate.has(voce.capability));
    const condizioni: CondizioniDelloStrumento = regalo
      ? 'regalo'
      : suo.canone_mensile_eur === voce.canone_mensile_eur
        ? (voce.canone_mensile_eur === 0 ? 'solo_consumo' : 'listino')
        : 'prezzo_proprio';
    righe.push({
      famiglia: 'strumento', id: voce.capability, etichetta: voce.etichetta, condizioni,
      importo_centesimi: regalo ? 0 : centesimi(suo.canone_mensile_eur),
    });
  }

  const somma = (f: (r: RigaDelCanone) => boolean) => righe.filter(f).reduce((s, r) => s + r.importo_centesimi, 0);
  const posti = somma((r) => r.famiglia === 'posti');
  const aggiunte = somma((r) => r.famiglia !== 'posti');
  const sconto = mappa(d.billing).discount_percent;
  return {
    righe,
    posti_centesimi: posti,
    aggiunte_centesimi: aggiunte,
    totale_centesimi: posti + aggiunte,
    sconto_percentuale: typeof sconto === 'number' && sconto > 0 ? sconto : null,
    senza_prezzo: senzaPrezzo,
  };
}

/**
 * Il canone entra davvero in cassa? (02/11/2026, Simone: «si deve vedere quali sono i soldi effettivamente che
 * entreranno … oppure quelli che hanno l'account gratuito fino a fine anno, e che dunque non sono soldi che
 * effettivamente arriveranno».) Stessa regola degli strumenti (`viaDiAttivazione`), senza una seconda lettura degli
 * stati di Stripe:
 *  - `paga`: abbonamento vivo fuori dalla prova, il canone si incassa ogni mese;
 *  - `in_prova`: prova gratuita in corso, con o senza abbonamento: il canone è solo potenziale fino a `fino_al_ms`;
 *  - `in_ritardo`: un pagamento è rimasto indietro (`past_due`, `unpaid`);
 *  - `in_pausa`: abbonamento in pausa;
 *  - `senza_abbonamento`: né abbonamento né prova (es. un'azienda dimostrativa): niente in cassa;
 *  - `esente`: non paga per decisione (`billing.exempt`);
 *  - `dimostrativa`: non è un cliente (`naturaDellAzienda`), vince su tutto il resto.
 */
export type IncassoDellAzienda =
  | { readonly stato: 'paga' }
  | { readonly stato: 'in_prova'; readonly fino_al_ms: number }
  | { readonly stato: 'in_ritardo' }
  | { readonly stato: 'in_pausa' }
  | { readonly stato: 'senza_abbonamento' }
  | { readonly stato: 'esente' }
  | { readonly stato: 'dimostrativa' };

/** PURA. */
export function incassoDellAzienda(datiAzienda: unknown, oraMs: number): IncassoDellAzienda {
  if (naturaDellAzienda(datiAzienda) === 'dimostrativa') return { stato: 'dimostrativa' };
  const via = viaDiAttivazione(aziendaPerGliStrumenti(datiAzienda), oraMs);
  switch (via.via) {
    case 'senza_addebito': return { stato: 'esente' };
    case 'nell_abbonamento':
      return via.gratis_fino_al_ms === null ? { stato: 'paga' } : { stato: 'in_prova', fino_al_ms: via.gratis_fino_al_ms };
    case 'gratis_fino_a_fine_prova': return { stato: 'in_prova', fino_al_ms: via.fino_al_ms };
    case 'pagamento_in_sospeso': return { stato: 'in_ritardo' };
    case 'abbonamento_in_pausa': return { stato: 'in_pausa' };
    case 'serve_il_pagamento': return { stato: 'senza_abbonamento' };
  }
}
